/**
 * Alpha compositing helpers.
 *
 * The design language uses one colour at many opacities rather than many
 * near-identical colours. Sampling the approved artwork confirmed this: every
 * light-green surface (status pills, "How It Works" step circles, the impact
 * card) is the same bright green at 20-25% opacity, and the text hierarchy is
 * one ink colour at descending opacities.
 *
 * Rule: never add a new hex value that could be expressed as an existing
 * seed colour at an opacity step. Add an opacity step instead.
 */

/** Opacity ladder. Every translucent surface in the product uses one of these. */
export const alpha = {
  /** Hairline dividers, subtle table row separators. */
  a04: 0.04,
  /** Card borders on cream, disabled fills. */
  a08: 0.08,
  /** Input borders, resting outlines. */
  a12: 0.12,
  /** Hover/pressed wash over a surface. */
  a16: 0.16,
  /** Status pill fills, step-circle fills, tinted info cards. */
  a20: 0.2,
  /** Emphasised pill fills (admin table badges). */
  a24: 0.24,
  /** Selected-state fills, focus rings. */
  a32: 0.32,
  /** Warning/amber fills, which need more body to read. */
  a40: 0.4,
  /** Disabled text, placeholder text. */
  a44: 0.44,
  /** Tertiary text, timestamps, helper copy. */
  a56: 0.56,
  /** Secondary text, field labels, descriptions. */
  a64: 0.64,
  /** Scrim behind the navigation drawer and modals. */
  a72: 0.72,
  /** Primary body text (softer than pure ink). */
  a88: 0.88,
  /** Fully opaque. */
  a100: 1,
} as const;

export type AlphaToken = keyof typeof alpha;
export type AlphaValue = (typeof alpha)[AlphaToken];

type Rgb = { r: number; g: number; b: number };

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);
const clamp255 = (n: number): number => (n < 0 ? 0 : n > 255 ? 255 : Math.round(n));

/** Parse `#rgb` or `#rrggbb` into channel values. Throws on malformed input. */
export function hexToRgb(hex: string): Rgb {
  const match = HEX_RE.exec(hex.trim());
  if (!match) {
    throw new Error(`Invalid hex colour: "${hex}". Expected #rgb or #rrggbb.`);
  }
  let body = match[1] ?? '';
  if (body.length === 3) {
    body = body
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const int = parseInt(body, 16);
  return { r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const hex = ((clamp255(r) << 16) | (clamp255(g) << 8) | clamp255(b))
    .toString(16)
    .padStart(6, '0');
  return `#${hex.toUpperCase()}`;
}

/**
 * Apply opacity to a colour, producing an `rgba()` string.
 *
 * Works everywhere: React Native accepts `rgba()` in style objects, and so
 * does CSS. Use this when the colour sits over an unknown or image background.
 */
export function withAlpha(hex: string, a: AlphaValue | number): string {
  const { r, g, b } = hexToRgb(hex);
  const value = clamp01(a);
  // Trim trailing zeros so the output stays readable in snapshots.
  const printed = String(Number(value.toFixed(4)));
  return `rgba(${r}, ${g}, ${b}, ${printed})`;
}

/**
 * Flatten a translucent colour against a known background into an opaque hex.
 *
 * Prefer this over {@link withAlpha} for large filled surfaces: opaque colours
 * avoid overdraw on React Native and never stack unexpectedly when two
 * translucent layers overlap.
 */
export function flatten(hex: string, a: AlphaValue | number, background: string): string {
  const fg = hexToRgb(hex);
  const bg = hexToRgb(background);
  const t = clamp01(a);
  return rgbToHex({
    r: fg.r * t + bg.r * (1 - t),
    g: fg.g * t + bg.g * (1 - t),
    b: fg.b * t + bg.b * (1 - t),
  });
}

/** Relative luminance per WCAG 2.1. */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two opaque colours. Range 1-21. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Pick whichever of two foregrounds contrasts better with `background`. */
export function readableOn(background: string, light: string, dark: string): string {
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}
